import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useUser } from "@/context/UserContext";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  acceptDoorToDoorRequest,
  rejectDoorToDoorRequest,
} from "@/lib/api";

const POLL_MS = 30000;

const relTime = (iso, t) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return t("notifbell.just_now");
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `${mins} ${t("notifbell.min_ago")}`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ${t("notifbell.hr_ago")}`;
  const days = Math.floor(hrs / 24);
  return `${days} ${t("notifbell.d_ago")}`;
};

// Adónde lleva cada aviso al pulsarlo (antes solo se marcaba como leído).
// El plan de movilidad se abre en el punto exacto si el aviso lo trae.
export const notificationTarget = (n, role) => {
  const k = n.kind || "";
  const hr = role === "hr_manager" || role === "super_admin";
  const planId = n.body_params?.plan_id;
  if (k === "demo_request") return "/admin/support";
  if (k === "support_reply") return "/?help=requests";
  if (k.startsWith("pmst_")) {
    if (planId) return `${hr ? "/admin/plan" : "/plan"}?plan=${planId}`;
    if (["pmst_deadline", "pmst_unknown", "pmst_paused", "pmst_followup_due", "pmst_followup_overdue"].includes(k)) {
      return hr ? "/admin/plan" : null;
    }
    return "/plan"; // designaciones y recordatorios de firma
  }
  return null;
};

const NotificationsBell = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const wrapRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const data = await listNotifications();
      setItems(data?.notifications || []);
      setUnread(data?.unread_count || 0);
    } catch {
      /* silent — bell stays static if endpoint hiccups */
    }
  }, []);

  // Initial + interval polling
  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, POLL_MS);
    return () => clearInterval(id);
  }, [fetchAll]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) fetchAll();
  };

  const handleMarkAll = async () => {
    setLoading(true);
    try {
      await markAllNotificationsRead();
      await fetchAll();
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = async (n, go = true) => {
    const target = go ? notificationTarget(n, user?.role) : null;
    if (target) { setOpen(false); navigate(target); }
    if (!n.read_at) {
      try {
        await markNotificationRead(n.id);
        setItems((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x))
        );
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* ignore */ }
    }
  };

  const handleAction = async (e, n, actionType) => {
    e.stopPropagation();
    if (processing === n.id) return;
    setProcessing(n.id);
    try {
      if (actionType === "accept") {
        await acceptDoorToDoorRequest(n.route_id);
      } else {
        await rejectDoorToDoorRequest(n.route_id);
      }
      await handleItemClick(n, false);
      fetchAll();
    } catch (err) {
      console.error(err);
      alert("Error processing request");
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid="navbar-notifications-bell"
        onClick={handleToggle}
        title={t("notifbell.aria")}
        aria-expanded={open}
        className="relative p-2 rounded-xl text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            data-testid="navbar-notifications-badge"
            className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[0.6875rem] font-bold flex items-center justify-center shadow-sm ring-2 ring-white"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-dropdown"
          className="absolute right-0 mt-2 w-80 sm:w-96 max-w-[92vw] bg-white border border-slate-200 rounded-2xl shadow-xl z-30 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <div className="text-sm font-semibold text-[#14532D]">{t("notifbell.title")}</div>
              <div className="text-[0.75rem] text-slate-500">{t("notifbell.subtitle")}</div>
            </div>
            <button
              type="button"
              onClick={handleMarkAll}
              disabled={loading || unread === 0}
              data-testid="notifications-mark-all-read"
              className="inline-flex items-center gap-1 text-[0.75rem] font-semibold text-[#15803D] hover:text-[#14532D] disabled:text-slate-300"
            >
              {loading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <CheckCheck className="h-3.5 w-3.5" />
              )}
              {t("notifbell.mark_all_read")}
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div
                data-testid="notifications-empty"
                className="px-4 py-8 text-center text-xs text-slate-400"
              >
                {t("notifbell.empty")}
              </div>
            ) : (
              items.map((n) => {
                const isUrgent = n.severity === "urgent";
                const isUnread = !n.read_at;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleItemClick(n)}
                    data-testid={`notification-item-${n.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-slate-50 transition-colors ${
                      isUnread ? "bg-[#F0FDF4]" : "bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`mt-1 h-2 w-2 rounded-full shrink-0 ${
                          isUrgent ? "bg-red-500" : isUnread ? "bg-[#16A34A]" : "bg-slate-200"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-semibold ${isUrgent ? "text-red-700" : "text-[#14532D]"}`}>
                          {t(n.title_key, n.body_params || {})}
                        </div>
                        <div className="text-[0.75rem] text-slate-600 mt-0.5 break-words whitespace-pre-line">
                          {t(n.body_key, n.body_params || {})}
                        </div>
                        <div className="text-[0.6875rem] text-slate-500 mt-1">
                          {relTime(n.created_at, t)}
                        </div>
                        {n.kind === "door_request" && isUnread && (
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              onClick={(e) => handleAction(e, n, "accept")}
                              disabled={processing === n.id}
                              className="px-3 py-1.5 bg-[#15803D] text-white text-[0.6875rem] font-bold rounded-lg hover:bg-[#166534] disabled:opacity-50"
                            >
                              {processing === n.id ? "..." : "Aceptar"}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleAction(e, n, "reject")}
                              disabled={processing === n.id}
                              className="px-3 py-1.5 bg-slate-100 text-slate-600 text-[0.6875rem] font-bold rounded-lg hover:bg-slate-200 disabled:opacity-50"
                            >
                              {processing === n.id ? "..." : "Rechazar"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
